import { policyClient } from '@api/sdk-client';
import axios from 'axios';

export type BillingInterval = 'month' | 'year';

// Public catalog: unauthenticated, same Stripe Price source WordPress reads. Amounts are minor units.
export interface PublicBillingCatalogPlan {
  planKey: string;
  interval: BillingInterval;
  amount: number;
  currency: string;
  trialDays: number;
  cardRequired: boolean;
}
export interface PublicBillingCatalog {
  market: 'usa' | 'ita';
  catalogVersion: string | null;
  plans: PublicBillingCatalogPlan[];
}

/** Recovery state of the signed-in buyer's own abandoned/unpaid checkout (jooice-lkvm). */
export type PendingPurchaseStatus = 'pending' | 'none' | 'processing' | 'expired' | 'support';

export interface PendingPurchaseResponse {
  status: PendingPurchaseStatus;
  /** Live checkout URL for an aborted payment; used once for the explicit Resume CTA. Never stored, never logged. */
  url?: string;
}

export interface BillingPlan {
  planKey: string;
  name: string;
  interval: BillingInterval;
  amount: number;
  currency: string;
  sellable: boolean;
  catalogVersion: string;
  changeability?: { allowed: boolean; reason?: 'core-managed' | 'non-admin' | 'not-sellable' | 'trial-interval' };
}

export interface PlanChangePreviewInput {
  targetPlanKey: string;
  interval: BillingInterval;
  addons: Array<{ planKey: string; quantity: number }>;
}
export interface PlanChangeItem {
  itemId: string | null;
  planKey: string | null;
  interval: BillingInterval | null;
  action: 'add' | 'swap' | 'remove';
  mode: 'immediate' | 'scheduled';
  effectiveAt: string;
}

export interface PlanChangePreviewResponse {
  amountDueNow: number;
  currency: string;
  nextIntervalAmount: number;
  /** Earliest affected date only; per-item dates are in `changes`. */
  effectiveAt: string;
  mode: 'immediate' | 'scheduled' | 'pending_update_possible';
  changes: PlanChangeItem[];
  lines: Array<{ description: string; amount: number; currency: string; quantity: number | null }>;
  warnings: string[];
  prorationDate?: number;
}

export interface PlanChangeApplyInput extends PlanChangePreviewInput {
  prorationDate?: number;
  idempotencyKey: string;
}

export interface PlanChangeApplyResponse {
  outcome: 'applied' | 'scheduled' | 'pending';
  effectiveAt: string;
  scheduleId?: string;
}

export interface PlanChangeReleaseResponse {
  outcome: 'released';
}

export type CustomerType = 'company' | 'freelancer';

export interface PurchaseAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
}

// Absent means unknown: optional fields are omitted, never sent empty. Policy owns validation.
export interface PurchaseCheckoutInput {
  planKey: string;
  interval: BillingInterval;
  market: 'usa' | 'ita';
  /** C2: the buyer's explicit choice travels only in this authenticated body. */
  marketingConsent: boolean;
  /** ita only. */
  customerType?: CustomerType;
  /** ita freelancer only. */
  taxCode?: string;
  agentCode?: string;
  partnerKey?: string;
  referrerCode?: string;
  attribution?: PurchaseAttribution;
}

export interface BillingSessionResponse {
  url: string;
}

export interface CreateTrialInput {
  companyName: string;
  market: 'ita' | 'usa';
}

export interface CreateTrialResponse {
  accountId: string;
  clerkOrgId: string;
  subscriptionId: string;
  trialEnd: string;
}

const billingAPI = {
  getPlans: async (): Promise<BillingPlan[]> => {
    const { data } = await policyClient.instance.get<BillingPlan[]>('/v1/me/billing/plans');
    return data;
  },

  previewPlanChange: async (input: PlanChangePreviewInput): Promise<PlanChangePreviewResponse> => {
    const { data } = await policyClient.instance.post<PlanChangePreviewResponse>(
      '/v1/me/billing/plan-change/preview',
      input,
    );
    return data;
  },

  applyPlanChange: async (input: PlanChangeApplyInput): Promise<PlanChangeApplyResponse> => {
    const { data } = await policyClient.instance.post<PlanChangeApplyResponse>('/v1/me/billing/plan-change', input);
    return data;
  },

  cancelScheduledChange: async (idempotencyKey: string): Promise<PlanChangeReleaseResponse> => {
    const { data } = await policyClient.instance.delete<PlanChangeReleaseResponse>(
      '/v1/me/billing/plan-change/scheduled',
      {
        headers: { 'idempotency-key': idempotencyKey },
      },
    );
    return data;
  },

  createPortalSession: async (): Promise<string> => {
    const { data } = await policyClient.instance.post<BillingSessionResponse>('/v1/me/billing/portal-session');
    return data.url;
  },
  createCheckoutSession: async (input: { planKey: string; interval: BillingPlan['interval'] }): Promise<string> => {
    const { data } = await policyClient.instance.post<BillingSessionResponse>('/v1/me/billing/checkout-session', input);
    return data.url;
  },
  getPendingPurchase: async (): Promise<PendingPurchaseResponse> => {
    const { data } = await policyClient.instance.get<PendingPurchaseResponse>('/v1/me/billing/pending-purchase');
    return data;
  },
  createPurchaseCheckoutSession: async (input: PurchaseCheckoutInput, token: string): Promise<string> => {
    const { data } = await axios.post<BillingSessionResponse>(
      `${import.meta.env.VITE_POLICY_URL}/v1/me/billing/purchase-checkout-session`,
      input,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return data.url;
  },
  createTrial: async (input: CreateTrialInput, suppressErrorToast = false): Promise<CreateTrialResponse> => {
    const { data } = await policyClient.instance.post<CreateTrialResponse>('/v1/me/trial', input, {
      suppressErrorToast,
    });
    return data;
  },

  getPublicCatalog: async (
    market: 'usa' | 'ita',
    selection?: { planKey: string; interval: BillingInterval },
  ): Promise<PublicBillingCatalog> => {
    const { data } = await axios.get<PublicBillingCatalog>(
      `${import.meta.env.VITE_POLICY_URL}/v1/public/billing/catalog`,
      {
        // planKey+interval together let Policy append that one hidden offer; omitted → public list only.
        params: selection ? { market, ...selection } : { market },
      },
    );
    return data;
  },
};

export default billingAPI;
